import { AxiosError } from 'axios'

import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'

import { UserInterface } from '@/interfaces/ServerInterfaces'

import APIServices from '../../api/APIServices'

export interface BusinessUsersQueryParams {
  page?: number
  per_page?: number
  search?: string
  roles?: string
  userId?: number
}

// Create a unique, descriptive key for this query
const BUSINESS_USERS_QUERY_KEY = 'business-users'

interface BusinessUsersResponse {
  items: UserInterface[]
  totalItems: number
  currentPage: number
  totalPages: number
}

// Define the query key type
type BusinessUsersQueryKey = readonly [
  string,
  { search?: string; roles?: string; businessId: number; userId?: number },
]

// Define proper types for infinite query options
type BusinessUsersInfiniteQueryOptions = UseInfiniteQueryOptions<
  BusinessUsersResponse,
  AxiosError,
  BusinessUsersResponse,
  BusinessUsersResponse,
  BusinessUsersQueryKey,
  number
>

/**
 * Fetch business users from the API
 * Separating API call logic from the hook for better testability
 */
const fetchBusinessUsers = async (
  params?: BusinessUsersQueryParams
): Promise<BusinessUsersResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServices.get('/security/users', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching business users
 *
 * @param params - Query parameters including businessId
 * @param options - TanStack Query options for customizing query behavior
 * @param businessId - Business ID for scoping
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useBusinessUsersQuery = ({
  params,
  options,
  businessId,
}: {
  params?: BusinessUsersQueryParams
  options?: Partial<UseQueryOptions<BusinessUsersResponse, AxiosError>>
  businessId: number
}) => {
  // Query hook
  const queryResult = useQuery<BusinessUsersResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: getBusinessUsersQueryKey({
      search: params?.search ?? '',
      roles: params?.roles ?? '',
      userId: params?.userId ?? undefined,
      businessId,
    }),

    // Separate the API call logic from the query configuration
    queryFn: () => fetchBusinessUsers(params),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  // Filter active users with valid data
  const activeBusinessUsers =
    queryResult.data?.items?.filter(
      (user) => user.isActive && user.id && user.displayName
    ) || []

  return {
    businessUsers: activeBusinessUsers,
    allBusinessUsers: queryResult.data?.items || [],
    isLoadingBusinessUsers: queryResult.isLoading,
    isErrorBusinessUsers: queryResult.isError,
    errorBusinessUsers: queryResult.error,
    refetchBusinessUsers: queryResult.refetch,
    isFetchingBusinessUsers: queryResult.isFetching,
    totalBusinessUsers: queryResult.data?.totalItems || 0,
    currentPage: queryResult.data?.currentPage || 1,
    totalPages: queryResult.data?.totalPages || 1,
  }
}

/**
 * Custom hook for infinite scrolling business users with search functionality
 * Following TanStack Query's infinite query pattern with optimized caching
 *
 * @param businessId - Business ID for scoping
 * @param params - Query parameters including search
 * @param options - TanStack Query options for customizing query behavior
 * @returns Object containing users, loading states, and utility functions
 */
export const useInfiniteBusinessUsersQuery = ({
  businessId,
  params = {},
  options,
}: {
  businessId: number
  params?: BusinessUsersQueryParams
  options?: Partial<BusinessUsersInfiniteQueryOptions>
}) => {
  const queryResult = useInfiniteQuery<
    BusinessUsersResponse,
    AxiosError,
    BusinessUsersResponse,
    BusinessUsersQueryKey,
    number
  >({
    queryKey: getBusinessUsersQueryKey({
      search: params?.search ?? '',
      roles: params?.roles ?? '',
      userId: params?.userId ?? undefined,
      businessId,
    }),
    queryFn: async ({ pageParam }) => {
      const result = await fetchBusinessUsers({
        ...params,
        page: pageParam,
        per_page: 20,
      })
      return result
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined
    },
    refetchOnWindowFocus: false,
    // Keep data fresh for 5 minutes (reduces unnecessary refetches)
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep inactive data in cache for 30 minutes (great for back/forward navigation)
    gcTime: 30 * 60 * 1000, // 30 minutes
    // Keep previous data while fetching new search results (better UX)
    placeholderData: (previousData) => previousData,
    // Retry failed requests (network resilience)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  })

  // Flatten all pages into a single array and filter active users
  const allUsers =
    (
      queryResult.data as { pages: BusinessUsersResponse[] } | undefined
    )?.pages?.flatMap((page: BusinessUsersResponse) => page?.items || []) || []

  const activeUsers = allUsers.filter(
    (user: UserInterface) => user.isActive && user.id && user.displayName
  )

  // Get total from first page (all pages have same total)
  const totalItems =
    (queryResult.data as { pages: BusinessUsersResponse[] } | undefined)
      ?.pages?.[0]?.totalItems || 0

  return {
    businessUsers: activeUsers,
    allBusinessUsers: allUsers,
    totalItems,
    loadedCount: allUsers?.length ?? 0,
    hasNextPage: queryResult.hasNextPage,
    isLoading: queryResult.isLoading,
    isLoadingMore: queryResult.isFetchingNextPage,
    isError: queryResult.isError,
    error: queryResult.error,
    fetchNextPage: queryResult.fetchNextPage,
    refetch: queryResult.refetch,
  }
}

/**
 * Helper function to get the business users query key
 * Useful for cache invalidation or prefetching
 */
export const getBusinessUsersQueryKey = ({
  businessId,
  search,
  roles,
  userId,
}: {
  businessId: number
  search?: string
  roles?: string
  userId?: number
}): BusinessUsersQueryKey => [
  BUSINESS_USERS_QUERY_KEY,
  { search, roles, businessId, userId },
]
